<script context='module'>
  import {
    getContext,
    setContext,
    onMount,
    createEventDispatcher,
  } from 'svelte';

  const DROPDOWN = Symbol('Dropdown');
  export const useDropdown = () => getContext(DROPDOWN);
</script>

<script>
  import { useDropdowns } from './DropdownRoot.svelte';
  import { writable } from 'svelte/store';
  import { browser } from '$app/environment';

  const addDropdown = useDropdowns();

  export let testid = undefined;

  /**
   * The element to anchor this dropdown to.
   */
  export let anchor;
  const anchor$ = writable(anchor);
  $: anchor$.set(anchor);

  /**
   * If we can click around while the dropdown is open
   */
  export let clickThrough = false;
  const clickThrough$ = writable(clickThrough);
  $: clickThrough$.set(clickThrough);

  /**
   * A two-word gravity description, such as 'top left' or 'bottom right'.
   */
  export let gravity = 'bottom left';
  const gravity$ = writable(gravity);
  $: gravity$.set(gravity);

  /**
   * A class to apply to the root dropdown element. Since dropdowns are actually in place in the
   * DOM, CSS cannot be applied normally.
   */
  export let className = '';
  const className$ = writable(className);
  $: className$.set(className);

  const dispatch = createEventDispatcher();
  let container;
  let entry;
  let mounted = false;

  let actionElement;
  export let focusOnDismiss = true;

  onMount(() => {
    const parent = container.parentNode;
    container.remove();
    mounted = true;
    entry = addDropdown(container, dispatch, {
      anchor: anchor$,
      gravity: gravity$,
      className: className$,
      clickThrough: clickThrough$,
    });
    return () => {
      mounted = false;
      parent.append(container);
    };
  });

  export function show() {
    actionElement = document.activeElement;
    entry.show();
  }

  export function dismiss() {
    entry.dismiss();
    if (focusOnDismiss) {
      actionElement?.focus();
    }
  }

  export function scrollToTop() {
    container.parentElement.scrollTo(0, 0);
  }

  export function getBounds() {
    return container.parentElement.getBoundingClientRect();
  }

  setContext(DROPDOWN, { dismiss });
</script>

{#if browser}
  <div class='dropdown-content' class:mounted bind:this={container} data-cy={testid}>
    <slot {dismiss} />
  </div>
{/if}

<style>
  .dropdown-content:not(.mounted) { display: none; }
</style>
